import { useEffect, useState, useCallback } from "react";
import { apiFetch, API_URL } from "../api/api";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

/* =================================================================
   HELPER
================================================================= */
const ID_MONTHS: Record<string, string> = {
  "01": "Januari", "02": "Februari", "03": "Maret",
  "04": "April",   "05": "Mei",      "06": "Juni",
  "07": "Juli",    "08": "Agustus",  "09": "September",
  "10": "Oktober", "11": "November", "12": "Desember",
};
function formatBulan(ym: string) {
  const [y, m] = ym.split("-");
  return `${ID_MONTHS[m] ?? m} ${y}`;
}

type RisikoUser = {
  user_id: number;
  nama: string;
  profil_nama: string;
  alamat: string;
  usia: number;
  usia_kehamilan_awal: number;
  tanggal: string;
  gdp: number;
  gd2j: number;
  ogtt: number;
};

/* =================================================================
   DASHBOARD PAGE
================================================================= */
export default function Dashboard() {
  const [bulan, setBulan] = useState("");
  const [availableMonths, setAvailableMonths] = useState<string[]>([]);
  const [stat, setStat]   = useState<any>(null);
  const [charts, setCharts] = useState<any>(null);

  // Risiko popup state
  const [showRisikoModal, setShowRisikoModal] = useState(false);
  const [risikoUsers, setRisikoUsers] = useState<RisikoUser[]>([]);
  const [loadingRisiko, setLoadingRisiko] = useState(false);

  /* ── fetch stat (with available_months on first load) ── */
  const fetchStat = useCallback((b: string) => {
    const url = b ? `/admin/dashboard?bulan=${b}` : "/admin/dashboard";
    apiFetch(url).then((res: any) => {
      setStat(res.data);
      if (res.data?.available_months?.length) {
        setAvailableMonths((prev) =>
          prev.length === 0 ? res.data.available_months : prev
        );
      }
    });
  }, []);

  /* ── fetch charts ── */
  const fetchCharts = useCallback((b: string) => {
    const url = b ? `/admin/dashboard/charts?bulan=${b}` : "/admin/dashboard/charts";
    apiFetch(url).then((res: any) => {
      if (res?.data) setCharts(res.data);
    });
  }, []);

  /* ── fetch risiko users ── */
  const fetchRisikoDetail = useCallback((b: string) => {
    setLoadingRisiko(true);
    const url = b ? `/admin/dashboard/risiko-detail?bulan=${b}` : "/admin/dashboard/risiko-detail";
    apiFetch(url)
      .then((res: any) => {
        setRisikoUsers(res.data ?? []);
      })
      .finally(() => setLoadingRisiko(false));
  }, []);

  /* initial load */
  useEffect(() => {
    fetchStat("");
    fetchCharts("");
  }, [fetchStat, fetchCharts]);

  /* filter change */
  const handleBulanChange = (b: string) => {
    setBulan(b);
    fetchStat(b);
    fetchCharts(b);
  };

  /* PDF export */
  const exportPDF = async () => {
    const token = localStorage.getItem("token");
    const url = bulan
      ? `${API_URL}/admin/dashboard/pdf?bulan=${bulan}`
      : `${API_URL}/admin/dashboard/pdf`;
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    window.open(URL.createObjectURL(blob));
  };

  /* loading skeleton */
  if (!stat || !charts) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-purple-500 border-t-transparent" />
      </div>
    );
  }

  const selectedLabel = bulan ? formatBulan(bulan) : "Keseluruhan";

  /* chart data */
  const activityData: { jenis: string; total: number }[] = charts.dailyActivity ?? [];
  const complianceData: { status: string; total: number }[] = charts.statusKepatuhan ?? [];

  const activityColors: Record<string, string> = {
    "Jalan Kaki":  "#8b5cf6",
    "Senam Hamil": "#ec4899",
    "Yoga Ringan": "#6366f1",
  };
  const complianceColors: Record<string, string> = {
    "Sangat Baik":      "#22c55e",
    "Cukup":            "#eab308",
    "Perlu Diperbaiki": "#ef4444",
  };

  return (
    <div className="space-y-8">
      {/* HEADER */}
      <div className="bg-white p-6 rounded-2xl shadow flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-purple-700">Dashboard Monitoring</h1>
          <p className="text-sm text-gray-500">Ringkasan kondisi ibu hamil SITIKOM</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <CalendarIcon className="w-4 h-4 text-purple-500 flex-shrink-0" />
            <select
              value={bulan}
              onChange={(e) => handleBulanChange(e.target.value)}
              className="border border-purple-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-purple-300 outline-none bg-white"
            >
              <option value="">Keseluruhan</option>
              {availableMonths.map((m) => (
                <option key={m} value={m}>{formatBulan(m)}</option>
              ))}
            </select>
          </div>
          <button
            onClick={exportPDF}
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-xl shadow transition text-sm"
          >
            <DownloadIcon className="w-4 h-4" /> Export PDF
          </button>
        </div>
      </div>

      {/* Filter badge */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-400">Periode:</span>
        <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">
          {selectedLabel}
        </span>
      </div>

      {/* STAT CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard title="Total Ibu" value={stat.total_ibu}
          gradient="from-purple-500 to-purple-700" icon={UsersIcon} />
        <StatCard title={bulan ? "Aktif Bulan Ini" : "Aktif Hari Terakhir"}
          value={stat.aktif_hari_ini}
          gradient="from-green-500 to-green-700" icon={ActivityIcon} />
        <StatCard title="Kepatuhan Rendah" value={stat.kepatuhan_rendah}
          gradient="from-yellow-500 to-orange-600" icon={WarningIcon} />
        <StatCard title="Risiko Gula Darah" value={stat.risiko_gula_darah}
          gradient="from-red-500 to-pink-600" icon={HeartPulseIcon} />
      </div>

      {/* CHART ROW 1 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Tren Kepatuhan */}
        <ChartCard title="📈 Tren Kepatuhan Mingguan">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={charts.weeklyTrend ?? []}>
              <XAxis dataKey="minggu" tickFormatter={(v) => `Mg ${v}`} />
              <YAxis domain={[0, 100]} unit="%" />
              <Tooltip formatter={(v: any) => [`${v}%`, "Rata-rata"]} />
              <Line dataKey="patuh" stroke="#8b5cf6" strokeWidth={3} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Aktivitas Keseluruhan — dari aktivitas_fisik */}
        <ChartCard title={`📊 Aktivitas Keseluruhan${bulan ? ` (${formatBulan(bulan)})` : ""}`}>
          {activityData.length === 0 ? (
            <div className="flex items-center justify-center h-[260px] text-gray-400 text-sm">Tidak ada data</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={activityData} barSize={48}>
                  <XAxis dataKey="jenis" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 200]} tickCount={6} />
                  <Tooltip formatter={(v: any) => [`${v} ibu`, "Jumlah"]} />
                  <Bar dataKey="total" radius={[8, 8, 0, 0]}>
                    {activityData.map((entry, i) => (
                      <Cell key={i} fill={activityColors[entry.jenis] ?? "#a78bfa"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="flex justify-center gap-4 mt-2 flex-wrap">
                {activityData.map((d, i) => (
                  <div key={i} className="flex items-center gap-1 text-xs text-gray-500">
                    <span className="inline-block w-3 h-3 rounded-full"
                      style={{ background: activityColors[d.jenis] ?? "#a78bfa" }} />
                    {d.jenis}: <strong className="text-gray-700 ml-0.5">{d.total}</strong>
                  </div>
                ))}
              </div>
            </>
          )}
        </ChartCard>
      </div>

      {/* CHART ROW 2 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Risiko Gula Darah Pie */}
        <ChartCard title="🩺 Risiko Gula Darah">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={[
                  { name: "Normal", value: (stat.total_ibu ?? 200) - (stat.risiko_gula_darah ?? 0) },
                  { name: "Risiko", value: stat.risiko_gula_darah ?? 0 },
                ]}
                dataKey="value"
                innerRadius={55}
                outerRadius={85}
                paddingAngle={4}
              >
                <Cell fill="#22c55e" />
                <Cell fill="#ef4444" />
              </Pie>
              <Legend />
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Status Kepatuhan — dari weekly_achievement_report */}
        <ChartCard title={`📌 Status Kepatuhan${bulan ? ` (${formatBulan(bulan)})` : ""}`}>
          {complianceData.length === 0 ? (
            <div className="flex items-center justify-center h-[220px] text-gray-400 text-sm">Tidak ada data</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={complianceData} barSize={52}>
                  <XAxis dataKey="status" tick={{ fontSize: 9 }} />
                  <YAxis domain={[0, 200]} tickCount={6} />
                  <Tooltip formatter={(v: any) => [`${v} ibu`, "Jumlah"]} />
                  <Bar dataKey="total" radius={[6, 6, 0, 0]}>
                    {complianceData.map((entry, i) => (
                      <Cell key={i} fill={complianceColors[entry.status] ?? "#c4b5fd"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="flex justify-center gap-3 mt-2 flex-wrap">
                {complianceData.map((d, i) => (
                  <div key={i} className="flex items-center gap-1 text-xs text-gray-500">
                    <span className="inline-block w-3 h-3 rounded-full"
                      style={{ background: complianceColors[d.status] ?? "#c4b5fd" }} />
                    {d.status}: <strong className="text-gray-700 ml-0.5">{d.total}</strong>
                  </div>
                ))}
              </div>
            </>
          )}
        </ChartCard>

        {/* Highlight Risiko + tombol popup */}
        <ChartCard title="❤️ Highlight Risiko">
          <div className="flex flex-col items-center justify-center h-[240px] gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-red-400/20 rounded-full blur-xl" />
              <p className="relative text-7xl font-black text-red-600">
                {stat.risiko_gula_darah ?? 0}
              </p>
            </div>
            <p className="text-sm text-gray-500 text-center">
              Ibu membutuhkan perhatian bidan
            </p>
            <p className="text-xs text-gray-400">
              dari {stat.total_ibu ?? 200} total responden
            </p>
            <button
              onClick={() => {
                fetchRisikoDetail(bulan);
                setShowRisikoModal(true);
              }}
              className="mt-1 flex items-center gap-2 bg-red-50 hover:bg-red-100 border border-red-200 text-red-700 px-4 py-2 rounded-xl text-sm font-semibold transition shadow-sm"
            >
              🔍 Lihat Nama Ibu Berisiko
            </button>
          </div>
        </ChartCard>
      </div>

      {/* RISIKO MODAL */}
      {showRisikoModal && (
        <RisikoModal
          users={risikoUsers}
          loading={loadingRisiko}
          bulan={bulan}
          onClose={() => setShowRisikoModal(false)}
        />
      )}
    </div>
  );
}

/* =================================================================
   RISIKO MODAL
================================================================= */
function RisikoModal({
  users, loading, bulan, onClose,
}: {
  users: RisikoUser[];
  loading: boolean;
  bulan: string;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[85vh] flex flex-col animate-fade-in">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="font-bold text-gray-800 text-lg flex items-center gap-2">
              🩺 Ibu Berisiko Gula Darah
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">
              {bulan ? formatBulan(bulan) : "Keseluruhan"} ·{" "}
              <span className="text-red-600 font-medium">{users.length} ibu terdeteksi risiko</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 font-bold text-lg transition"
          >
            ×
          </button>
        </div>

        {/* Info bar */}
        <div className="px-6 py-3 bg-red-50 border-b border-red-100 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          <p className="text-xs text-red-600">
            GDP ≥ 100 mg/dL atau GD2J ≥ 140 mg/dL atau OGTT ≥ 140 mg/dL = Risiko Diabetes Gestasional
          </p>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-4 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-red-400 border-t-transparent" />
            </div>
          ) : users.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <span className="text-4xl mb-2">✅</span>
              <p className="text-sm">Tidak ada ibu dengan risiko gula darah</p>
              <p className="text-xs mt-1">pada periode {bulan ? formatBulan(bulan) : "ini"}</p>
            </div>
          ) : (
            users.map((u, i) => (
              <div
                key={u.user_id ?? i}
                className="flex items-start gap-3 p-4 rounded-xl bg-white border border-red-100 hover:border-red-300 hover:shadow-sm transition"
              >
                {/* Avatar */}
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-red-400 to-pink-500 flex items-center justify-center text-white font-bold text-base flex-shrink-0 shadow">
                  {(u.profil_nama || u.nama || "?")[0].toUpperCase()}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-800 truncate">{u.profil_nama || u.nama}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {u.alamat ?? "–"} {u.usia ? `· ${u.usia} th` : ""}
                    {u.usia_kehamilan_awal ? ` · Kehamilan: ${u.usia_kehamilan_awal} minggu` : ""}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {u.gdp != null && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${u.gdp >= 100 ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-600"}`}>
                        GDP: {u.gdp} mg/dL
                      </span>
                    )}
                    {u.gd2j != null && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${u.gd2j >= 140 ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-600"}`}>
                        GD2J: {u.gd2j} mg/dL
                      </span>
                    )}
                    {u.ogtt != null && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${u.ogtt >= 140 ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-600"}`}>
                        OGTT: {u.ogtt} mg/dL
                      </span>
                    )}
                  </div>
                </div>

                {/* Badge + date */}
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span className="px-2 py-1 bg-red-500 text-white text-xs font-bold rounded-full">
                    RISIKO
                  </span>
                  <span className="text-xs text-gray-400">{u.tanggal}</span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-2.5 rounded-xl font-semibold text-sm transition"
          >
            Tutup
          </button>
        </div>
      </div>
    </div>
  );
}

/* =================================================================
   REUSABLE COMPONENTS
================================================================= */
function StatCard({ title, value, gradient, icon: Icon }: any) {
  return (
    <div className={`relative overflow-hidden rounded-2xl p-5 text-white shadow-xl bg-gradient-to-br ${gradient} hover:scale-[1.03] transition-all`}>
      <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/20 rounded-full blur-2xl" />
      <div className="absolute top-4 right-4 bg-white/20 p-3 rounded-xl">
        <Icon className="w-7 h-7 text-white" />
      </div>
      <p className="text-sm opacity-90">{title}</p>
      <p className="text-4xl font-bold mt-2">{value ?? 0}</p>
    </div>
  );
}

function ChartCard({ title, children }: any) {
  return (
    <div className="bg-white p-5 rounded-2xl shadow">
      <h3 className="font-semibold text-gray-700 mb-3 text-sm">{title}</h3>
      {children}
    </div>
  );
}

/* ICONS */
function UsersIcon(p: any) {
  return <svg {...p} viewBox="0 0 24 24" fill="currentColor"><path d="M17 11c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zm-7-2a4 4 0 10-8 0 4 4 0 008 0zM3 18c0-2.21 3.13-4 7-4 .96 0 1.87.13 2.71.34C11.66 15.2 11 16.52 11 18v1H3v-1zm14 0v-1c0-2.21-3.13-4-7-4h-.15C10.63 13.37 11 14.16 11 15v3h6z"/></svg>;
}
function ActivityIcon(p: any) {
  return <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>;
}
function WarningIcon(p: any) {
  return <svg {...p} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L1 21h22L12 2zm1 15h-2v-2h2v2zm0-4h-2v-4h2v4z" /></svg>;
}
function HeartPulseIcon(p: any) {
  return <svg {...p} viewBox="0 0 24 24" fill="currentColor"><path d="M12 21s-7-4.35-9.5-8.5C.5 9.5 2.5 6 6 6c2 0 3.5 1.5 4 2.5C10.5 7.5 12 6 14 6c3.5 0 5.5 3.5 3.5 6.5C19 16.65 12 21 12 21z" /></svg>;
}
function DownloadIcon(p: any) {
  return <svg {...p} viewBox="0 0 24 24" fill="currentColor"><path d="M12 3v12m0 0l4-4m-4 4l-4-4M4 17h16v4H4z" /></svg>;
}
function CalendarIcon(p: any) {
  return <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
}
